import type { InventoryScopeType } from '../api';

/**
 * Escopos do inventário — rótulos e listas dinâmicas (issue #463).
 *
 * O wizard tem duas listas: a do escopo e a do item dentro do escopo.
 * A segunda só existe quando o escopo não é `Geral`, recebe um rótulo
 * próprio e é alimentada por um cadastro diferente conforme o escopo:
 *
 *  - AREA: áreas organizacionais (o backend expande para os centros de
 *    custo da área e das subáreas)
 *  - LOCATION: localizações
 *  - CUSTODIAN: responsáveis
 *  - CLIENT: clientes
 *  - PROJECT: projetos
 *  - CATEGORY: categorias patrimoniais
 *
 * O módulo é puro (sem React) para poder ser coberto por teste unitário.
 */

export interface EscopoOpcao {
  value: string;
  label: string;
}

/** Rótulo do escopo no select e no resumo da etapa 2. */
export const SCOPE_LABELS: Record<InventoryScopeType, string> = {
  all: 'Geral (todos os ativos)',
  area: 'Área organizacional',
  location: 'Localização específica',
  custodian: 'Responsável',
  client: 'Cliente',
  project: 'Projeto',
  category: 'Categoria patrimonial',
};

/** Versão curta para a coluna "Escopo" da listagem. */
export const SCOPE_SUMMARY: Record<InventoryScopeType, string> = {
  all: 'Geral',
  area: 'Área',
  location: 'Localização',
  custodian: 'Responsável',
  client: 'Cliente',
  project: 'Projeto',
  category: 'Categoria',
};

/** Rótulo contextual da segunda lista. */
export const SCOPE_ITEM_LABELS: Record<Exclude<InventoryScopeType, 'all'>, string> = {
  area: 'Nome da área',
  location: 'Nome da localização',
  custodian: 'Nome do responsável',
  client: 'Nome do cliente',
  project: 'Nome do projeto',
  category: 'Nome da categoria',
};

/** Mensagem de validação quando o item obrigatório não foi escolhido. */
export const SCOPE_ITEM_REQUIRED_MESSAGES: Record<Exclude<InventoryScopeType, 'all'>, string> = {
  area: 'Selecione a área organizacional.',
  location: 'Selecione a localização.',
  custodian: 'Selecione o responsável.',
  client: 'Selecione o cliente.',
  project: 'Selecione o projeto.',
  category: 'Selecione a categoria.',
};

/** Opções de cada cadastro, na ordem em que aparecem no formulário. */
export interface EscopoFontes {
  areas: EscopoOpcao[];
  localizacoes: EscopoOpcao[];
  responsaveis: EscopoOpcao[];
  clientes: EscopoOpcao[];
  projetos: EscopoOpcao[];
  categorias: EscopoOpcao[];
}

export const ESCOPO_FONTES_VAZIAS: EscopoFontes = {
  areas: [],
  localizacoes: [],
  responsaveis: [],
  clientes: [],
  projetos: [],
  categorias: [],
};

/** `true` quando o escopo exige a escolha de um item (tudo menos Geral). */
export function escopoExigeItem(escopo: InventoryScopeType): escopo is Exclude<InventoryScopeType, 'all'> {
  return escopo !== 'all';
}

/** Opções da segunda lista — vazia para Geral, que não exibe o campo. */
export function opcoesDoItem(escopo: InventoryScopeType, fontes: EscopoFontes): EscopoOpcao[] {
  switch (escopo) {
    case 'area':
      return fontes.areas;
    case 'location':
      return fontes.localizacoes;
    case 'custodian':
      return fontes.responsaveis;
    case 'client':
      return fontes.clientes;
    case 'project':
      return fontes.projetos;
    case 'category':
      return fontes.categorias;
    case 'all':
      return [];
  }
}

/** Rótulo da segunda lista; `null` para Geral. */
export function rotuloDoItem(escopo: InventoryScopeType): string | null {
  return escopoExigeItem(escopo) ? SCOPE_ITEM_LABELS[escopo] : null;
}

/** Mensagem de erro da segunda lista; `null` para Geral. */
export function mensagemItemObrigatorio(escopo: InventoryScopeType): string | null {
  return escopoExigeItem(escopo) ? SCOPE_ITEM_REQUIRED_MESSAGES[escopo] : null;
}

/**
 * Nome do item escolhido, para o resumo da etapa 2. Cai no próprio id
 * quando a opção ainda não chegou (ex.: lista carregando).
 */
export function nomeDoItem(
  escopo: InventoryScopeType,
  refId: string,
  fontes: EscopoFontes,
): string | null {
  if (!escopoExigeItem(escopo) || !refId) return null;
  return opcoesDoItem(escopo, fontes).find((o) => o.value === refId)?.label ?? refId;
}
